import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { subscriptions } from '../../api/client'
import PageHeader from '../../components/PageHeader'
import { PageLayout, Card, Badge, Modal, Skeleton, EmptyCard } from '../../components/ui'

function formatPrice(cents) {
  if (cents == null) return '$0.00'
  return `$${(cents / 100).toFixed(2)}`
}

function cadenceLabel(cadence) {
  switch (cadence) {
    case 'monthly': return '/mo'
    case 'annual': return '/yr'
    default: return ''
  }
}

function PlanIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
    </svg>
  )
}

function PlanCard({ plan, onEdit, onDeactivate }) {
  const features = plan.features ? plan.features.split('\n').filter(f => f.trim()) : []

  return (
    <div className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-lume-teal/20 text-lume-teal rounded-full flex items-center justify-center flex-shrink-0">
            <PlanIcon className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-sm font-medium text-gray-900 dark:text-indices-bright truncate">
                {plan.name}
              </p>
              {plan.badge && (
                <Badge variant="primary" size="sm">{plan.badge}</Badge>
              )}
              <Badge variant={plan.isActive ? 'primary' : 'default'} size="sm">
                {plan.isActive ? 'Active' : 'Inactive'}
              </Badge>
            </div>
            {plan.description && (
              <p className="text-xs text-gray-500 dark:text-indices-muted mt-0.5 line-clamp-2">
                {plan.description}
              </p>
            )}
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-lg font-semibold text-primary-600 dark:text-lume-teal font-display">
            {formatPrice(plan.priceInCents)}
            <span className="text-xs font-normal text-gray-500 dark:text-indices-muted">
              {cadenceLabel(plan.billingCadence)}
            </span>
          </p>
        </div>
      </div>

      {features.length > 0 && (
        <div className="mt-3 ml-13 pl-0.5">
          <ul className="space-y-1">
            {features.slice(0, 3).map((f, i) => (
              <li key={i} className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-indices-muted">
                <svg className="w-3 h-3 text-lume-teal flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                {f.trim()}
              </li>
            ))}
            {features.length > 3 && (
              <li className="text-xs text-gray-400 dark:text-indices-muted">
                +{features.length - 3} more
              </li>
            )}
          </ul>
        </div>
      )}

      <div className="flex gap-2 mt-3 ml-13">
        <button
          onClick={() => onEdit(plan)}
          className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-indices-medium bg-gray-100 dark:bg-dial-elevated border border-gray-200 dark:border-dial-border rounded-lg hover:bg-gray-200 dark:hover:bg-dial-surface transition-colors"
        >
          Edit
        </button>
        {plan.isActive && (
          <button
            onClick={() => onDeactivate(plan)}
            className="px-3 py-1.5 text-xs font-medium text-status-error bg-status-error/10 border border-status-error/20 rounded-lg hover:bg-status-error/20 transition-colors"
          >
            Deactivate
          </button>
        )}
      </div>
    </div>
  )
}

function PlanFormModal({ isOpen, onClose, plan }) {
  const queryClient = useQueryClient()
  const isEditing = !!plan

  const [form, setForm] = useState({
    name: plan?.name || '',
    description: plan?.description || '',
    price: plan ? (plan.priceInCents / 100).toFixed(2) : '',
    billingCadence: plan?.billingCadence || 'monthly',
    features: plan?.features || '',
    badge: plan?.badge || '',
    displayOrder: plan?.displayOrder ?? 0,
  })
  const [error, setError] = useState(null)

  const mutation = useMutation({
    mutationFn: (data) => isEditing ? subscriptions.updatePlan(data) : subscriptions.createPlan(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptionPlans'] })
      onClose()
      setError(null)
    },
    onError: (err) => {
      setError(err.message || `Failed to ${isEditing ? 'update' : 'create'} plan`)
    },
  })

  const handleSubmit = (e) => {
    e.preventDefault()
    setError(null)

    if (!form.name || !form.price) {
      setError('Name and price are required')
      return
    }

    const priceInCents = Math.round(parseFloat(form.price) * 100)
    if (isNaN(priceInCents) || priceInCents < 0) {
      setError('Please enter a valid price')
      return
    }

    const data = {
      name: form.name,
      description: form.description || undefined,
      priceInCents,
      billingCadence: form.billingCadence,
      features: form.features || undefined,
      badge: form.badge || undefined,
      displayOrder: parseInt(form.displayOrder, 10) || 0,
    }

    if (isEditing) {
      data.id = plan.id
    }

    mutation.mutate(data)
  }

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }))
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={isEditing ? 'Edit Plan' : 'New Subscription Plan'} size="md">
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-3 bg-status-error/10 border border-status-error/30 rounded-xl text-sm text-status-error">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-indices-medium mb-1.5">
            Plan Name <span className="text-status-error">*</span>
          </label>
          <input
            type="text"
            value={form.name}
            onChange={handleChange('name')}
            placeholder="e.g., Pro Monthly"
            className="w-full px-4 py-3 rounded-xl bg-white dark:bg-dial-surface border border-gray-300 dark:border-dial-border text-gray-900 dark:text-indices-bright placeholder:text-gray-400 dark:placeholder:text-indices-muted focus:border-primary-500 dark:focus:border-lume-teal focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-lume-teal/20"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-indices-medium mb-1.5">
            Description
          </label>
          <textarea
            value={form.description}
            onChange={handleChange('description')}
            rows={2}
            placeholder="Short description of what this plan includes..."
            className="w-full px-4 py-3 rounded-xl bg-white dark:bg-dial-surface border border-gray-300 dark:border-dial-border text-gray-900 dark:text-indices-bright placeholder:text-gray-400 dark:placeholder:text-indices-muted focus:border-primary-500 dark:focus:border-lume-teal focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-lume-teal/20 resize-none"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-indices-medium mb-1.5">
              Price ($) <span className="text-status-error">*</span>
            </label>
            <input
              type="number"
              value={form.price}
              onChange={handleChange('price')}
              min="0"
              step="0.01"
              placeholder="0.00"
              className="w-full px-4 py-3 rounded-xl bg-white dark:bg-dial-surface border border-gray-300 dark:border-dial-border text-gray-900 dark:text-indices-bright placeholder:text-gray-400 dark:placeholder:text-indices-muted focus:border-primary-500 dark:focus:border-lume-teal focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-lume-teal/20"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-indices-medium mb-1.5">
              Billing Cadence
            </label>
            <select
              value={form.billingCadence}
              onChange={handleChange('billingCadence')}
              className="w-full px-4 py-3 rounded-xl bg-white dark:bg-dial-surface border border-gray-300 dark:border-dial-border text-gray-900 dark:text-indices-bright focus:border-primary-500 dark:focus:border-lume-teal focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-lume-teal/20 appearance-none"
            >
              <option value="monthly">Monthly</option>
              <option value="annual">Annual</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-indices-medium mb-1.5">
            Features (one per line)
          </label>
          <textarea
            value={form.features}
            onChange={handleChange('features')}
            rows={3}
            placeholder={"Unlimited AI coaching\nCustom meal plans\nWeekly check-ins"}
            className="w-full px-4 py-3 rounded-xl bg-white dark:bg-dial-surface border border-gray-300 dark:border-dial-border text-gray-900 dark:text-indices-bright placeholder:text-gray-400 dark:placeholder:text-indices-muted focus:border-primary-500 dark:focus:border-lume-teal focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-lume-teal/20 resize-none"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-indices-medium mb-1.5">
              Badge Text
            </label>
            <input
              type="text"
              value={form.badge}
              onChange={handleChange('badge')}
              placeholder="e.g., Popular"
              className="w-full px-4 py-3 rounded-xl bg-white dark:bg-dial-surface border border-gray-300 dark:border-dial-border text-gray-900 dark:text-indices-bright placeholder:text-gray-400 dark:placeholder:text-indices-muted focus:border-primary-500 dark:focus:border-lume-teal focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-lume-teal/20"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-indices-medium mb-1.5">
              Display Order
            </label>
            <input
              type="number"
              value={form.displayOrder}
              onChange={handleChange('displayOrder')}
              min="0"
              placeholder="0"
              className="w-full px-4 py-3 rounded-xl bg-white dark:bg-dial-surface border border-gray-300 dark:border-dial-border text-gray-900 dark:text-indices-bright placeholder:text-gray-400 dark:placeholder:text-indices-muted focus:border-primary-500 dark:focus:border-lume-teal focus:ring-2 focus:ring-primary-500/20 dark:focus:ring-lume-teal/20"
            />
          </div>
        </div>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={mutation.isPending}
            className="flex-1 py-2.5 px-4 bg-dial-elevated text-indices-bright border border-dial-border rounded-xl font-medium hover:bg-dial-surface transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="flex-1 py-2.5 px-4 bg-lume-teal text-dial-black rounded-xl font-medium hover:bg-lume-green hover:shadow-lume transition-all disabled:opacity-50"
          >
            {mutation.isPending ? (isEditing ? 'Saving...' : 'Creating...') : (isEditing ? 'Save Changes' : 'Create Plan')}
          </button>
        </div>
      </form>
    </Modal>
  )
}

export default function SubscriptionPlansPage() {
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [editingPlan, setEditingPlan] = useState(null)

  const { data, isLoading } = useQuery({
    queryKey: ['subscriptionPlans'],
    queryFn: subscriptions.listPlans,
  })

  const deactivateMutation = useMutation({
    mutationFn: (plan) => subscriptions.updatePlan({ id: plan.id, isActive: false }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subscriptionPlans'] })
    },
  })

  const plans = data?.plans || data || []
  const planList = Array.isArray(plans) ? plans : []

  const handleEdit = (plan) => {
    setEditingPlan(plan)
    setShowForm(true)
  }

  const handleDeactivate = (plan) => {
    if (confirm(`Deactivate "${plan.name}"? New clients won't be able to select this plan.`)) {
      deactivateMutation.mutate(plan)
    }
  }

  const handleCloseForm = () => {
    setShowForm(false)
    setEditingPlan(null)
  }

  return (
    <PageLayout>
      <PageHeader
        title="Subscription Plans"
        showBack
        backTo="/billing"
        actions={
          <button
            onClick={() => { setEditingPlan(null); setShowForm(true) }}
            className="px-3 py-1.5 bg-lume-teal text-dial-black rounded-lg text-sm font-medium hover:bg-lume-green hover:shadow-lume transition-all"
          >
            + New Plan
          </button>
        }
      />

      <div className="px-4 py-4 space-y-4">
        {/* Summary */}
        <div className="grid grid-cols-3 gap-3">
          <Card>
            <p className="text-xs text-gray-500 dark:text-indices-muted">Total Plans</p>
            <p className="text-xl font-bold text-primary-600 dark:text-lume-teal mt-1 font-display">
              {planList.length}
            </p>
          </Card>
          <Card>
            <p className="text-xs text-gray-500 dark:text-indices-muted">Active</p>
            <p className="text-xl font-bold text-primary-600 dark:text-lume-teal mt-1 font-display">
              {planList.filter(p => p.isActive).length}
            </p>
          </Card>
          <Card>
            <p className="text-xs text-gray-500 dark:text-indices-muted">Inactive</p>
            <p className="text-xl font-bold text-gray-500 dark:text-indices-muted mt-1 font-display">
              {planList.filter(p => !p.isActive).length}
            </p>
          </Card>
        </div>

        {/* Plan List */}
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-24 rounded-xl" />
            <Skeleton className="h-24 rounded-xl" />
            <Skeleton className="h-24 rounded-xl" />
          </div>
        ) : planList.length === 0 ? (
          <EmptyCard
            icon={<PlanIcon className="w-6 h-6" />}
            title="No subscription plans"
            description="Create your first subscription plan to offer recurring billing to clients"
            action={
              <button
                onClick={() => { setEditingPlan(null); setShowForm(true) }}
                className="px-4 py-2 bg-lume-teal text-dial-black rounded-xl font-medium hover:bg-lume-green hover:shadow-lume transition-all"
              >
                Create Plan
              </button>
            }
          />
        ) : (
          <Card padding="none">
            <div className="divide-y divide-gray-200 dark:divide-dial-border">
              {planList.map(plan => (
                <PlanCard
                  key={plan.id}
                  plan={plan}
                  onEdit={handleEdit}
                  onDeactivate={handleDeactivate}
                />
              ))}
            </div>
          </Card>
        )}
      </div>

      {showForm && (
        <PlanFormModal
          isOpen={showForm}
          onClose={handleCloseForm}
          plan={editingPlan}
        />
      )}
    </PageLayout>
  )
}
